import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { ResponsiveLine } from '@nivo/line';

/** One line: a value per date (YYYY-MM-DD); null leaves a gap. `note` is extra text for the tip. */
export type TrendSeries = {
  id: string;
  color: string;
  points: { x: string; y: number | null; note?: string }[];
};

type Props = {
  series: TrendSeries[];
  height?: number;
  /** Values are percentages (0–100). */
  percent?: boolean;
  /** Dated notes drawn as dashed upright lines, for before and after. */
  markers?: { date: string; label: string }[];
  /** How the tip names a point's date (moment format); monthly points read better as "Jul 2026". */
  dateFormat?: string;
  /** Name the lines even when there is only one (e.g. a single target). */
  legend?: boolean;
};

// Fewer date labels the longer the span, so they never crowd.
const dateTicks = (days: number) =>
  days <= 14
    ? { tickValues: 'every day', format: '%b %d' }
    : days <= 100
    ? { tickValues: 'every week', format: '%b %d' }
    : days <= 400
    ? { tickValues: 'every month', format: '%b' }
    : { tickValues: 'every 3 months', format: '%b %Y' };

/** A small line graph over dates for the analytics pages (Usage, First week). */
const TrendLine = ({ series, height = 220, percent, markers, dateFormat = 'ddd, MMM D, YYYY', legend }: Props) => {
  const dates = series.flatMap(line => line.points.map(point => point.x)).sort();
  if (!dates.length) return null;
  const first = moment(dates[0]);
  const last = moment(dates[dates.length - 1]);
  const formatValue = (value: number) => (percent ? `${Math.round(value)}%` : Math.round(value).toLocaleString());
  const notes = (markers || []).filter(marker => !moment(marker.date).isBefore(first) && !moment(marker.date).isAfter(last));

  return (
    <div>
      {(legend || series.length > 1) && (
        <Legend>
          {series.map(line => (
            <LegendItem key={line.id}>
              <Dot style={{ backgroundColor: line.color }} />
              {line.id}
            </LegendItem>
          ))}
        </Legend>
      )}
      <div style={{ height }}>
        <ResponsiveLine
          data={series.map(line => ({ id: line.id, data: line.points }))}
          colors={series.map(line => line.color)}
          margin={{ top: 12, right: 24, bottom: 32, left: 48 }}
          xScale={{ type: 'time', format: '%Y-%m-%d', useUTC: false, precision: 'day' }}
          xFormat="time:%Y-%m-%d"
          yScale={{ type: 'linear', min: 0, max: 'auto' }}
          axisBottom={dateTicks(last.diff(first, 'days'))}
          axisLeft={{ tickValues: 4, format: (value: any) => formatValue(Number(value)) }}
          enableGridX={false}
          gridYValues={4}
          curve="linear"
          lineWidth={2}
          pointSize={series[0].points.length > 60 ? 0 : 4}
          useMesh
          markers={notes.map(note => ({
            axis: 'x' as const,
            value: moment(note.date).toDate(),
            legend: note.label,
            lineStyle: { stroke: '#bfbfbf', strokeWidth: 1, strokeDasharray: '4 4' },
            textStyle: { fontSize: 10, fill: '#8c8c8c' }
          }))}
          tooltip={({ point }) => {
            const datum: any = point.data;
            return (
              <Tip>
                <strong>{moment(datum.x).format(dateFormat)}</strong>
                <div>
                  {series.length > 1 && `${point.serieId}: `}
                  {formatValue(Number(datum.y))}
                </div>
                {datum.note && <Muted>{datum.note}</Muted>}
              </Tip>
            );
          }}
        />
      </div>
    </div>
  );
};

export default TrendLine;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 12px;
  margin-bottom: 4px;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const Dot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
`;

const Tip = styled.div`
  background: white;
  padding: 6px 10px;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  font-size: 12px;
`;

const Muted = styled.div`
  color: rgba(0, 0, 0, 0.45);
`;
